import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSkipPreliminaryInvoiceReview } from './useSkipPreliminaryInvoiceReview';
import { useReviewPreliminaryInvoice } from './useReviewPreliminaryInvoice';
import CustomModal from '../components/informationPopup/customModal';
import { showToast } from '../components/utils/toast';
import { IActionDto } from '../interface/common';
import { useShowVersionConflict } from './useVersionConflict';

const PRELIMINARY_INVOICE_QUERY_KEYS = ['preliminary-invoices-list', 'preliminary-invoice-details'];

enum ErrorThrowingQuery {
  REVIEW = 'review',
  SKIP = 'skip',
}

export const usePreliminaryInvoiceActions = ({
  navigateOnSuccess,
}: {
  navigateOnSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const [preliminaryInvoice, setPreliminaryInvoice] = useState<IActionDto>();
  const [reviewModal, setReviewModal] = useState<boolean>(false);
  const [skipModal, setSkipModal] = useState<boolean>(false);
  const [isReviewError, setIsReviewError] = useState<boolean>(false);
  const [isSkipError, setIsSkipError] = useState<boolean>(false);
  const showVersionConflict = useShowVersionConflict();

  const onError = (error: unknown, errorThrowingQuery: ErrorThrowingQuery) => {
    if (showVersionConflict(error, PRELIMINARY_INVOICE_QUERY_KEYS)) return;

    if (errorThrowingQuery === ErrorThrowingQuery.REVIEW) {
      setTimeout(() => {
        setIsReviewError(true);
      }, 500);
    } else if (errorThrowingQuery === ErrorThrowingQuery.SKIP) {
      setTimeout(() => {
        setIsSkipError(true);
      }, 500);
    }
  };

  const { mutate: skipPreliminaryInvoiceReview, reset: resetSkipReviewError } =
    useSkipPreliminaryInvoiceReview();

  const { mutate: reviewPreliminaryInvoice, reset: resetReviewError } =
    useReviewPreliminaryInvoice();

  const onSkipPreliminaryInvoiceReview = (preliminaryInvoice: IActionDto) => {
    skipPreliminaryInvoiceReview(preliminaryInvoice, {
      onSuccess: () => {
        showToast('success', t('preliminaryInvoice:successfullySkipped'));
        navigateOnSuccess();
      },
      onError: (error) => {
        onError(error, ErrorThrowingQuery.SKIP);
      },
    });
  };
  const onReviewPreliminaryInvoice = (preliminaryInvoice: IActionDto) => {
    reviewPreliminaryInvoice(preliminaryInvoice, {
      onSuccess: () => {
        showToast('success', t('preliminaryInvoice:successfullyReviewed'));
        navigateOnSuccess();
      },
      onError: (error) => {
        onError(error, ErrorThrowingQuery.REVIEW);
      },
    });
  };
  const onReview = (payload: IActionDto) => {
    setPreliminaryInvoice(payload);
    setReviewModal(true);
  };

  const onSkip = (payload: IActionDto) => {
    setPreliminaryInvoice(payload);
    setSkipModal(true);
  };

  const ReviewModal = useMemo(
    () => (
      <CustomModal
        visible={reviewModal}
        title={t('preliminaryInvoice:markPreliminaryInvoiceAsReviewed')}
        description={t('preliminaryInvoice:preliminaryInvoiceSkipOrReviewText')}
        confirmText={t('preliminaryInvoice:review')}
        onConfirm={() => {
          if (preliminaryInvoice) {
            onReviewPreliminaryInvoice(preliminaryInvoice);
          }
        }}
        cancelText={t('common:cancel')}
        onClose={() => {
          setReviewModal(false);
        }}
      />
    ),
    [reviewModal],
  );

  const SkipModal = useMemo(
    () => (
      <CustomModal
        visible={skipModal}
        title={t('preliminaryInvoice:skipPreliminaryInvoiceReview')}
        description={t('preliminaryInvoice:preliminaryInvoiceSkipOrReviewText')}
        confirmText={t('preliminaryInvoice:skip')}
        onConfirm={() => {
          setSkipModal(false);
          if (preliminaryInvoice) {
            onSkipPreliminaryInvoiceReview(preliminaryInvoice);
          }
        }}
        onClose={() => {
          setSkipModal(false);
        }}
        cancelText={t('common:cancel')}
      />
    ),
    [skipModal],
  );

  const ErrorModal = useMemo(
    () => (
      <CustomModal
        visible={isReviewError || isSkipError}
        onConfirm={() => {
          resetReviewError();
          resetSkipReviewError();
          setIsSkipError(false);
          setIsReviewError(false);
        }}
        title={t('preliminaryInvoice:notAllowedToReviewOrSkipPreliminaryInvoice')}
        confirmText={t('common:ok')}
      />
    ),
    [isReviewError, isSkipError],
  );

  return {
    onReview,
    onSkip,
    ErrorModal,
    SkipModal,
    ReviewModal,
  };
};
